import { IconChevronRight, IconFileTypePdf, IconLoader2, IconPhoto, IconPrinter } from '@tabler/icons-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { cn } from '@/lib/utils';
import { useTranslation } from '../../i18n/hooks/useTranslation';
import { toBnDigits } from '../../utils/bnFormat';

/**
 * "Download & print" — one tap per format (PDF, image, direct print). While one is being made the others wait.
 * `imageLabel` is the image format shown on the option ('PNG' / 'JPG'); `meta` the line under the title (paper · pages).
 */
export default function PosterExportDialog({ open, onOpenChange, busy, onRun, pageCount, meta, imageLabel = 'PNG' }) {
    const { t, i18n } = useTranslation();
    const n = i18n.language?.startsWith('en') ? String(pageCount) : toBnDigits(pageCount);
    const options = [
        { kind: 'pdf', Icon: IconFileTypePdf, tint: 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400', title: t('posterEditor.export.pdf'), desc: t('posterEditor.export.pdfDesc') },
        { kind: 'image', Icon: IconPhoto, tint: 'bg-emerald-50 text-emerald-600 dark:bg-emerald-900/20 dark:text-emerald-400', title: t('posterEditor.export.image', { format: imageLabel }), desc: t('posterEditor.export.imageDesc', { count: pageCount, n }) },
        { kind: 'print', Icon: IconPrinter, tint: 'bg-primary/10 text-primary', title: t('posterEditor.export.print'), desc: t('posterEditor.export.printDesc') },
    ];

    return (
        // z-[260]: above the full-screen editor window (z-200) it can be opened from
        <Dialog open={open} onOpenChange={(next) => { if (!busy) onOpenChange(next); }}>
            <DialogContent className="w-[calc(100%-2rem)] max-w-[500px] z-[260] p-0 overflow-hidden rounded-2xl" overlayClassName="z-[259]">
                <DialogHeader className="px-5 pt-5 pb-3 mb-0 pr-12">
                    <DialogTitle className="text-base">{t('posterEditor.export.title')}</DialogTitle>
                    <DialogDescription>{meta}</DialogDescription>
                </DialogHeader>
                <div className="px-5 pb-5 flex flex-col gap-2.5">
                    {options.map(({ kind, Icon, tint, title, desc }) => (
                        <button
                            key={kind}
                            type="button"
                            disabled={!!busy}
                            onClick={() => onRun(kind)}
                            className={cn(
                                'flex items-center gap-3.5 p-3.5 rounded-xl border border-zinc-200 dark:border-zinc-700 text-left transition-colors',
                                busy ? 'opacity-60 cursor-not-allowed' : 'hover:bg-zinc-50 dark:hover:bg-zinc-800',
                            )}
                        >
                            <span className={cn('w-11 h-11 shrink-0 rounded-xl flex items-center justify-center', tint)}><Icon size={22} stroke={1.8} /></span>
                            <span className="flex-1 min-w-0">
                                <span className="block text-sm font-semibold text-zinc-800 dark:text-zinc-100">{title}</span>
                                <span className="block text-xs text-zinc-500 dark:text-zinc-400 leading-snug mt-0.5">{desc}</span>
                            </span>
                            {busy === kind ? <IconLoader2 size={18} className="animate-spin text-primary shrink-0" /> : <IconChevronRight size={18} className="text-zinc-400 shrink-0" />}
                        </button>
                    ))}
                </div>
            </DialogContent>
        </Dialog>
    );
}
